// tableUtils.js
// A utility file for markdown table processing and formatting
// Place this in the same directory as GameResultsModal.js

import React from 'react';

/**
 * Preprocesses markdown text to properly format tables
 * Works with any sport, any team, any analysis format
 * 
 * @param {string} text - The markdown text to process
 * @return {string} - The processed markdown with properly formatted tables
 */
export const preprocessMarkdown = (text) => {
  if (!text) return '';
  
  let processedText = text;
  
  // Step 1: Detect text that looks like a table structure using pipe characters
  const pipeTablePattern = /(\|[\s\w:,.\-+()]*\|[\s\w:,.\-+()]*\|\n)(?:\|[\s\w:,.\-+()]*\|[\s\w:,.\-+()]*\|\n)+/g;
  
  processedText = processedText.replace(pipeTablePattern, (match) => {
    // Check if it's already a well-formatted markdown table
    if (match.includes('|-') || match.includes('|:')) {
      return match; // Already formatted properly
    }
    
    // Format as proper markdown table
    const lines = match.trim().split('\n');
    if (lines.length < 2) return match;
    
    // Extract headers from first line by splitting on pipes
    const headerLine = lines[0].trim();
    const headers = headerLine.split('|')
      .filter(col => col.trim() !== '')
      .map(col => col.trim());
    
    if (headers.length < 2) return match;
    
    // Build markdown table with headers
    let markdownTable = `| ${headers.join(' | ')} |\n`;
    
    // Add separator line with appropriate dashes
    markdownTable += '|';
    headers.forEach(header => {
      markdownTable += ` ${'-'.repeat(Math.max(3, header.length))} |`;
    });
    markdownTable += '\n';
    
    // Add data rows, starting from second line
    for (let i = 1; i < lines.length; i++) {
      if (lines[i].trim() === '') continue;
      
      const cols = lines[i].split('|')
        .filter(col => col.trim() !== '')
        .map(col => col.trim());
      
      if (cols.length >= 2) {
        markdownTable += `| ${cols.join(' | ')} |\n`;
      }
    }
    
    // Add table marker if not present
    if (!match.includes('*Table')) {
      markdownTable = '*Table\n' + markdownTable;
    }
    
    return markdownTable;
  });
  
  // Step 2: Look for lines that have a key-value structure that could be converted to tables
  const keyValuePattern = /(?:^|\n)([\w\s]+):\s*([\w\s+-]+)(?:\n([\w\s]+):\s*([\w\s+-]+)){2,}/g;
  
  processedText = processedText.replace(keyValuePattern, (match) => {
    // Don't convert if it's already inside a table
    if (match.includes('|')) return match;
    
    // Extract all key-value pairs
    const pairs = [];
    const regex = /([\w\s]+):\s*([\w\s+-]+)/g;
    let pair;
    
    while ((pair = regex.exec(match)) !== null) {
      pairs.push({
        key: pair[1].trim(),
        value: pair[2].trim()
      });
    }
    
    if (pairs.length < 3) return match; // Need at least 3 pairs to consider it a table
    
    // Build a markdown table
    let markdownTable = '*Table\n';
    markdownTable += '| Factor | Value |\n';
    markdownTable += '|--------|-------|\n';
    
    pairs.forEach(pair => {
      markdownTable += `| ${pair.key} | ${pair.value} |\n`;
    });
    
    return markdownTable;
  });
  
  // Step 3: Look for lines that might be headers for tables that follow
  processedText = processedText.replace(/(?:^|\n)(?:\*\*?)?([A-Z][\w\s]+(?:Table|Comparison|Analysis|Factors|Breakdown|Advantage))(?:\*\*?)?:?(?:\*\*?)?\s*\n+\|/g, (match, tableName) => {
    return `\n*Table: ${tableName}\n|`;
  });
  
  // Step 4: Add markdown code blocks around any remaining non-markdown table structures
  processedText = processedText.replace(/(?<!`{3}[\s\S]*?)(?<=\n|^)(\|[\s\w:,.\-+]*\|[\s\w:,.\-+]*\|\n)(?!\|[-:])/g, (match) => {
    return '```\n' + match + '```\n';
  });
  
  return processedText;
};

/**
 * Custom components for ReactMarkdown to properly style tables
 * Works with any sport and any analysis format
 */
export const tableComponents = {
  table: ({node, ...props}) => (
    <div className="my-6 rounded-lg overflow-hidden border border-emerald-500/30 bg-slate-800/50">
      <table className="w-full text-left" {...props} />
    </div>
  ),
  
  thead: ({node, ...props}) => (
    <thead className="bg-emerald-500/20 text-emerald-300 border-b border-emerald-500/30" {...props} />
  ),
  
  tbody: ({node, ...props}) => (
    <tbody className="divide-y divide-slate-700/50" {...props} />
  ),
  
  tr: ({node, ...props}) => (
    <tr className="hover:bg-emerald-500/5 transition-colors" {...props} />
  ),
  
  th: ({node, ...props}) => (
    <th className="px-4 py-3 font-medium text-emerald-300" {...props} />
  ),
  
  td: ({node, ...props}) => {
    const text = String(props.children);
    // Generic detection of positive/negative/neutral values
    let colorClass = "text-slate-300";
    
    // Check for common patterns in different sports analysis
    if (text.match(/^\s*\+\s*(?:Major|Strong|High|Significant)/i)) {
      colorClass = "text-emerald-400 font-medium";
    } else if (text.match(/^\s*\+\s*(?:Moderate|Medium|Good)/i)) {
      colorClass = "text-emerald-300";
    } else if (text.match(/^\s*\+\s*(?:Mild|Minor|Slight|Small)/i)) {
      colorClass = "text-emerald-200";
    } else if (text.match(/^\s*(?:Neutral|Even|Balanced|None)/i)) {
      colorClass = "text-slate-400";
    } else if (text.match(/^\s*\-\s*(?:Mild|Minor|Slight|Small)/i)) {
      colorClass = "text-orange-300";
    } else if (text.match(/^\s*\-\s*(?:Moderate|Medium|Notable)/i)) {
      colorClass = "text-orange-400";
    } else if (text.match(/^\s*\-\s*(?:Major|Strong|High|Significant)/i)) {
      colorClass = "text-red-400 font-medium";
    } else if (text.match(/(?:advantage|edge|favor)/i)) {
      colorClass = "text-emerald-300";
    } else if (text.match(/(?:disadvantage|weakness|poor)/i)) {
      colorClass = "text-orange-300";
    }
    
    return <td className={`px-4 py-3 ${colorClass}`} {...props} />;
  },
  
  // Table-related paragraph styling
  p: ({node, children, ...props}) => {
    const text = String(children);
    
    // Check for generic table header indicators
    if (text.match(/^\s*\*?\*?Table|Comparison|Analysis|Factors|Matrix|Grid/i)) {
      return (
        <div className="mt-6 mb-2">
          <h3 className="text-lg font-bold text-emerald-400 text-left">{text.replace(/^\*+/, '')}</h3>
        </div>
      );
    }
    
    // Check if this paragraph contains prediction indicators
    if (text.match(/(?:Prediction|Winner|Recommendation|Pick|Selection|Projected Winner|Best Bet):/i)) {
      return (
        <div className="my-4 p-3 bg-emerald-500/10 border border-emerald-500/20 rounded-lg">
          <p className="font-medium text-white text-left" {...props}>{children}</p>
        </div>
      );
    }
    
    // Style for Confidence Level
    if (text.match(/(?:Confidence|Certainty|Conviction|Probability)(?:\s*Level)?:/i)) {
      return (
        <p className="mb-3 text-emerald-400 font-semibold text-left" {...props}>{children}</p>
      );
    }
    
    return <p className="mb-3 text-slate-300 text-left" {...props}>{children}</p>;
  },
};

/**
 * Applies standard text replacements for prediction analysis terms
 * to ensure consistent formatting
 * 
 * @param {string} text - The original prediction text
 * @return {string} - Text with standardized formatting
 */
export const applyStandardReplacements = (text) => {
  if (!text) return '';
  
  return text
    // Section headers
    .replace(/Predictive Feature Model and Simulation/g, '\n## Predictive Feature Model and Simulation\n')
    .replace(/Simulation Results \((\d+)x Runs\)/g, '\n## Simulation Results ($1x Runs)\n')
    // Generic result terms
    .replace(/(?:Most Predictive|Key) Features:/gi, '**Most Predictive Features:**')
    .replace(/(?:Cumulative|Total) Edge Score:/gi, '**Cumulative Edge Score:**')
    .replace(/(?:Confidence|Certainty) Level:/gi, '**Confidence Level:**')
    .replace(/(?:Predicted|Projected|Expected) Winner:/gi, '**Predicted Winner:**')
    // Sport-specific terms
    .replace(/Pitching Matchup:/g, '**Pitching Matchup:**')
    .replace(/Offensive Power:/g, '**Offensive Power:**')
    .replace(/Recent Form:/g, '**Recent Form:**')
    .replace(/Injury Reports:/g, '**Injury Reports:**');
};